import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Cookie, X, Settings, Check } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'jigsaw_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'jigsaw_cookie_preferences';

export default function CookieConsent() {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Slight delay before showing banner
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(localStorage.getItem(COOKIE_PREFERENCES_KEY) || '{}');
        setPreferences({ ...preferences, ...saved });
      } catch (e) {
        console.error('Failed to parse cookie preferences');
      }
    }
  }, []);

  const saveConsent = (acceptAll = false) => {
    const finalPreferences = acceptAll ? {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true
    } : preferences;

    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(finalPreferences));
    
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
      detail: finalPreferences
    }));

    setShowBanner(false);
    setShowPreferences(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences({ ...preferences, [key]: !preferences[key] });
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Cookie className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900">{t('cookie.title')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('cookie.descPrefix')}<a href="/cookies" className="text-blue-600 hover:underline">{t('cookie.policyLink')}</a>{t('cookie.descSuffix')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreferences(!showPreferences)}
              >
                <Settings className="w-4 h-4 mr-1" />
                {t('cookie.customise')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveConsent(false)}
              >
                {t('cookie.rejectAll')}
              </Button>
              <Button
                size="sm"
                onClick={() => saveConsent(true)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {t('cookie.acceptAll')}
              </Button>
            </div>
          </div>

          {/* Preferences Panel */}
          {showPreferences && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Necessary */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{t('cookie.necessary')}</span>
                    <div className="w-10 h-5 bg-green-500 rounded-full flex items-center justify-end px-1">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('cookie.necessaryDesc')}
                  </p>
                </div>

                {/* Analytics */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{t('cookie.analytics')}</span>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-10 h-5 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        preferences.analytics ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('cookie.analyticsDesc')}
                  </p>
                </div>

                {/* Marketing */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{t('cookie.marketing')}</span>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`w-10 h-5 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        preferences.marketing ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('cookie.marketingDesc')}
                  </p>
                </div>

                {/* Personalization */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{t('cookie.personalization')}</span>
                    <button
                      onClick={() => togglePreference('personalization')}
                      className={`w-10 h-5 rounded-full transition-colors ${
                        preferences.personalization ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        preferences.personalization ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('cookie.personalizationDesc')}
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button onClick={() => saveConsent(false)} size="sm">
                  {t('cookie.savePreferences')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop for preferences */}
      {showPreferences && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setShowPreferences(false)}
        />
      )}
    </>
  );
}

// Utility to check consent status
export const hasConsent = (type) => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return false;
    
    const prefs = JSON.parse(localStorage.getItem(COOKIE_PREFERENCES_KEY) || '{}');
    return prefs[type] === true;
  } catch (e) {
    return false;
  }
};

// Hook to listen for consent changes
export const useCookieConsent = () => {
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Load initial preferences
    try {
      const saved = JSON.parse(localStorage.getItem(COOKIE_PREFERENCES_KEY) || '{}');
      setConsent({ ...consent, ...saved });
    } catch (e) {}

    // Listen for updates
    const handler = (e) => setConsent(e.detail);
    window.addEventListener('cookieConsentUpdated', handler);
    return () => window.removeEventListener('cookieConsentUpdated', handler);
  }, []);

  return consent;
};
